import React, { useState, useRef, useEffect } from "react";
// import "./dateTimeRange.css";

const pad = (n) => String(n).padStart(2, "0");

const todayDate = new Date();
const currentYear = todayDate.getFullYear();
const currentMonth = todayDate.getMonth();

const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Time Panel Component ──────────────────────────────────────────
function TimePanel({ title, dateStr, time, onChange }) {
  const [h, m] = (time || "00:00").split(":").map(Number);
  const hoursRef = useRef();
  const minsRef = useRef();

  useEffect(() => {
    const scrollIfRef = (ref, val) => {
      if (ref.current) {
        const el = ref.current.querySelector(`[data-val="${val}"]`);
        if (el) el.scrollIntoView({ block: "center" });
      }
    };
    scrollIfRef(hoursRef, h);
    scrollIfRef(minsRef, m);
  }, [time, h, m]);

  const updateTime = (nh, nm) => onChange(`${pad(nh)}:${pad(nm)}`);

  return (
    <div className="drp-time-panel">
      <div className="drp-time-header">
        {dateStr ? dateStr.replace(/-/g, "/") : title}
      </div>
      <div className="drp-time-cols">
        <div className="drp-time-col" ref={hoursRef}>
          {Array.from({ length: 24 }, (_, i) => (
            <div
              key={i} data-val={i}
              className={`drp-time-item ${i === h ? "active" : ""}`}
              onClick={() => updateTime(i, m)}
            >
              {pad(i)}
            </div>
          ))}
        </div>
        <div className="drp-time-col" ref={minsRef}>
          {Array.from({ length: 60 }, (_, i) => (
            <div
              key={i} data-val={i}
              className={`drp-time-item ${i === m ? "active" : ""}`}
              onClick={() => updateTime(h, i)}
            >
              {pad(i)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main DateRangePicker Component ──────────────────────────────────────────
const DateRangePicker = ({ startTime, endTime, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("date");
  const [leftCal, setLeftCal] = useState({ year: currentYear, month: currentMonth });
  const [selecting, setSelecting] = useState("start");

  const [tempStart, setTempStart] = useState(null);
  const [tempEnd, setTempEnd] = useState(null);
  const [tempStartTime, setTempStartTime] = useState("00:00");
  const [tempEndTime, setTempEndTime] = useState("23:59");
  const [hoverDate, setHoverDate] = useState(null);

  const wrapRef = useRef();

  const rightCal = (() => {
    let { year, month } = leftCal;
    if (month === 11) return { year: year + 1, month: 0 };
    return { year, month: month + 1 };
  })();

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    if (startTime && typeof startTime === 'string') {
      const [d, t] = startTime.split("T");
      setTempStart(d || null);
      setTempStartTime(t ? t.substring(0, 5) : "00:00");
    }
    if (endTime && typeof endTime === 'string') {
      const [d, t] = endTime.split("T");
      setTempEnd(d || null);
      setTempEndTime(t ? t.substring(0, 5) : "23:59");
    }
    setView("date");
    setOpen(true);
  };

  const toDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const handleManualInputChange = (type, val) => {
    // Regex updated for DD/MM/YYYY HH:mm
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})$/;
    const match = val.match(regex);

    if (match) {
      const [_, d, m, y, hh, mm] = match;
      const dateStr = `${y}-${m}-${d}`;
      const timeStr = `${hh}:${mm}`;

      if (type === "start") {
        setTempStart(dateStr);
        setTempStartTime(timeStr);
      } else {
        setTempEnd(dateStr);
        setTempEndTime(timeStr);
      }
    }
  };

  const prevMonth = () => setLeftCal((prev) => prev.month === 0 ? { year: prev.year - 1, month: 11 } : { ...prev, month: prev.month - 1 });
  const nextMonth = () => setLeftCal((prev) => prev.month === 11 ? { year: prev.year + 1, month: 0 } : { ...prev, month: prev.month + 1 });
  const prevYear = () => setLeftCal((prev) => ({ ...prev, year: prev.year - 1 }));
  const nextYear = () => setLeftCal((prev) => ({ ...prev, year: prev.year + 1 }));

  const handleDayClick = (ds) => {
    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(ds);
      setTempEnd(null);
      setSelecting("end");
    } else {
      if (ds < tempStart) {
        setTempEnd(tempStart);
        setTempStart(ds);
      } else {
        setTempEnd(ds);
      }
      setSelecting("start");
    }
  };

  const handleOk = () => {
    if (tempStart && tempEnd) {
      onChange({
        startTime: `${tempStart}T${tempStartTime}`,
        endTime: `${tempEnd}T${tempEndTime}`,
      });
      setOpen(false);
    }
  };

  const renderCalendar = (year, month) => {
    const cells = [];
    const first = firstDayOfMonth(year, month);
    const days = daysInMonth(year, month);
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);

    return (
      <div className="drp-calendar">
        <div className="drp-weekdays">
          {WEEKDAYS.map(w => <span key={w}>{w}</span>)}
        </div>
        <div className="drp-days">
          {cells.map((d, i) => {
            if (!d) return <span key={`e-${i}`} className="drp-day disabled"></span>;
            const ds = `${year}-${pad(month + 1)}-${pad(d)}`;
            const isStart = ds === tempStart;
            const isEnd = ds === tempEnd;
            const isToday = ds === `${currentYear}-${pad(currentMonth + 1)}-${pad(todayDate.getDate())}`;

            let inRange = false;
            if (tempStart && tempEnd) {
              inRange = ds > tempStart && ds < tempEnd;
            } else if (tempStart && hoverDate && selecting === "end") {
              const [lo, hi] = tempStart < hoverDate ? [tempStart, hoverDate] : [hoverDate, tempStart];
              inRange = ds > lo && ds < hi;
            }

            let classes = "drp-day";
            if (isToday) classes += " today";
            if (isStart || isEnd) classes += " selected";
            if (inRange) classes += " in-range";

            return (
              <span key={d} className={classes} onClick={() => handleDayClick(ds)} onMouseEnter={() => setHoverDate(ds)}>
                {d}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  // FIX: Type check added to prevent .split() error
  const formatDisplay = (dtStr) => {
    if (!dtStr || typeof dtStr !== 'string') return "";
    try {
      const [d, t] = dtStr.split("T");
      if (!d || !t) return dtStr;
      // Helper function use karke format change kiya
      return `${toDDMMYYYY(d)} ${t.substring(0, 5)}`;
    } catch (e) {
      return "";
    }
  };

  return (
    <div className="drp-wrapper" ref={wrapRef}>
      <div className={`drp-input ${disabled ? "disabled" : ""} ${open ? "focused" : ""}`} onClick={handleOpen}>
        <span style={{ fontSize: '15px', color: '#9fa5ac' }}>
          {(startTime && endTime) ? `${formatDisplay(startTime)} ~ ${formatDisplay(endTime)}` : "Select date range"}
        </span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </div>

      {open && (
        <div className="drp-dropdown">
          <div className="drp-header">
            <input
              key={tempStart + tempStartTime}
              className="drp-header-input"
              defaultValue={tempStart ? `${toDDMMYYYY(tempStart)} ${tempStartTime}` : ""}
              placeholder="DD/MM/YYYY HH:mm"
              onBlur={(e) => handleManualInputChange("start", e.target.value)}
            />
            <span className="drp-sep">~</span>
            <input
              key={tempEnd + tempEndTime}
              className="drp-header-input"
              defaultValue={tempEnd ? `${toDDMMYYYY(tempEnd)} ${tempEndTime}` : ""}
              placeholder="DD/MM/YYYY HH:mm"
              onBlur={(e) => handleManualInputChange("end", e.target.value)}
            />
          </div>

          {view === "date" ? (
            <>
              <div className="drp-nav">
                <div className="drp-nav-group"><button onClick={prevYear}>&laquo;</button><button onClick={prevMonth}>&lsaquo;</button></div>
                <div className="drp-month-title">{MONTHS[leftCal.month]} {leftCal.year}</div>
                <div className="drp-month-title">{MONTHS[rightCal.month]} {rightCal.year}</div>
                <div className="drp-nav-group"><button onClick={nextMonth}>&rsaquo;</button><button onClick={nextYear}>&raquo;</button></div>
              </div>
              <div className="drp-calendars-container">
                {renderCalendar(leftCal.year, leftCal.month)}
                {renderCalendar(rightCal.year, rightCal.month)}
              </div>
            </>
          ) : (
            <div className="drp-time-panels">
              <TimePanel title="Start" dateStr={tempStart} time={tempStartTime} onChange={setTempStartTime} />
              <TimePanel title="End" dateStr={tempEnd} time={tempEndTime} onChange={setTempEndTime} />
            </div>
          )}

          <div className="drp-footer">
            <button className="drp-link-btn" onClick={() => setView(view === "date" ? "time" : "date")}>
              {view === "date" ? "select time" : "select date"}
            </button>
            <button className="drp-ok-btn" onClick={handleOk} disabled={!tempStart || !tempEnd}>Ok</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;